/**
 * User Management Tab - Extracted from admin.tsx per ADR-039: Component Size Strategy
 * This component handles user listing, search, and role assignment
 */

import type { UserRoleResponse, UserWithRoles } from "@/types/models"
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  Input,
  Spacer,
  Stack,
  Text,
  createToaster,
} from "@chakra-ui/react"
import { useState } from "react"
import { FiEdit, FiTrash, FiUserCheck, FiUserX } from "react-icons/fi"

import { Button } from "@/components/atoms/Button"
import { useRoles } from "@/hooks/api/useRoles"
import { useRoleAssignment, useUsersWithRoles } from "@/hooks/api/useUsers"
import { api } from "@/services/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import AddUser from "../AddUser"
import { UserRoleManagementModal } from "../modals/UserRoleManagementModal"

export function UserManagementTab() {
  const [searchQuery, setSearchQuery] = useState("")
  const [editingUser, setEditingUser] = useState<UserWithRoles | null>(null)
  const { data: usersWithRoles, isLoading } = useUsersWithRoles()
  const { assignRole, removeRole } = useRoleAssignment()
  const { data: roles = [] } = useRoles()
  const queryClient = useQueryClient()
  const toaster = createToaster({
    placement: "top-end",
  })

  // User management mutations
  const activateUserMutation = useMutation({
    mutationFn: (userId: string) =>
      api.admin.activateUserApiV1AdminUsersUserIdActivatePost({ userId }),
    onSuccess: (_, userId) => {
      toaster.create({
        title: "User activated successfully",
        type: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] })
      queryClient.invalidateQueries({ queryKey: ["admin", "user", userId] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: "Failed to activate user",
        description: error?.message || "Unknown error",
        type: "error",
        duration: 3000,
      })
    },
  })

  const deactivateUserMutation = useMutation({
    mutationFn: (userId: string) =>
      api.admin.deactivateUserApiV1AdminUsersUserIdDeactivatePost({ userId }),
    onSuccess: (_, userId) => {
      toaster.create({
        title: "User deactivated successfully",
        type: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] })
      queryClient.invalidateQueries({ queryKey: ["admin", "user", userId] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: "Failed to deactivate user",
        description: error?.message || "Unknown error",
        type: "error",
        duration: 3000,
      })
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) =>
      api.admin.deleteUserApiV1AdminUsersUserIdDelete({ userId }),
    onSuccess: () => {
      toaster.create({
        title: "User deleted successfully",
        type: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: "Failed to delete user",
        description: error?.message || "Unknown error",
        type: "error",
        duration: 3000,
      })
    },
  })

  const handleRoleAssign = async (userId: string, roleId: string) => {
    try {
      await assignRole({
        userId: userId,
        roleId: roleId,
        assignment_reason: "Assigned via Admin Dashboard",
      })

      // Update local editingUser state to immediately reflect the change
      if (editingUser && editingUser.id === userId) {
        const assignedRole = roles?.find((role) => role.id === roleId)
        if (assignedRole) {
          const newUserRole = {
            role: assignedRole,
            assigned_at: new Date().toISOString(),
            assigned_by: null,
          }
          setEditingUser({
            ...editingUser,
            roles: [...(editingUser.roles || []), newUserRole],
          })
        }
      }

      toaster.create({
        title: "Role assigned successfully",
        type: "success",
        duration: 3000,
      })
    } catch (error) {
      toaster.create({
        title: "Failed to assign role",
        type: "error",
        duration: 3000,
      })
    }
  }

  const handleRoleRemove = async (userId: string, roleId: string) => {
    try {
      await removeRole({ userId, roleId })

      // Update local editingUser state to immediately reflect the change
      if (editingUser && editingUser.id === userId) {
        setEditingUser({
          ...editingUser,
          roles:
            editingUser.roles?.filter(
              (userRole) => userRole.role.id !== roleId,
            ) || [],
        })
      }

      toaster.create({
        title: "Role removed successfully",
        type: "success",
        duration: 3000,
      })
    } catch (error) {
      toaster.create({
        title: "Failed to remove role",
        type: "error",
        duration: 3000,
      })
    }
  }

  const handleDeleteUser = (user: UserWithRoles) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete ${user.full_name || user.email}? This action cannot be undone.`,
      )
    ) {
      deleteUserMutation.mutate(user.id)
    }
  }

  // Filter users based on search query
  const filteredUsers =
    usersWithRoles?.filter(
      (user: UserWithRoles) =>
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.full_name.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || []

  if (isLoading) {
    return (
      <Card.Root>
        <Card.Header>
          <Flex>
            <Stack align="start" gap={1}>
              <Heading size="md">User Management</Heading>
              <Text fontSize="sm" color="gray.600">
                Manage system users and their basic information
              </Text>
            </Stack>
            <Spacer />
            <Flex gap={3} align="center">
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                maxW="300px"
              />
              <AddUser />
            </Flex>
          </Flex>
        </Card.Header>
        <Card.Body>
          <Stack gap={4} align="stretch">
            <Box overflowX="auto">
              <Box minW="800px">
                {/* Table Header */}
                <Flex
                  bg="gray.50"
                  p={3}
                  borderBottom="2px"
                  borderColor="gray.200"
                  fontWeight="semibold"
                >
                  <Box flex="1">Full Name</Box>
                  <Box flex="1">Email</Box>
                  <Box flex="1.5">Roles</Box>
                  <Box w="100px">Permissions</Box>
                  <Box w="100px">Status</Box>
                  <Box w="100px">Actions</Box>
                </Flex>

                {/* Loading Rows */}
                <Stack gap={0} align="stretch">
                  <Text p={4} textAlign="center" color="gray.500">
                    Loading users...
                  </Text>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Card.Body>
      </Card.Root>
    )
  }

  if (filteredUsers.length === 0 && !searchQuery) {
    return (
      <Card.Root>
        <Box p={8} textAlign="center">
          <Stack gap={4}>
            <Box fontSize="4xl" color="gray.400">
              <FiUserX />
            </Box>
            <Stack gap={2}>
              <Heading size="md" color="gray.600">
                No users found
              </Heading>
              <Text color="gray.500">There are no users in the system yet</Text>
            </Stack>
          </Stack>
        </Box>
      </Card.Root>
    )
  }

  return (
    <Card.Root>
      <Card.Header>
        <Flex>
          <Stack align="start" gap={1}>
            <Heading size="md">User Management</Heading>
            <Text fontSize="sm" color="gray.600">
              Manage system users and their basic information
            </Text>
          </Stack>
          <Spacer />
          <Flex gap={3} align="center">
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              maxW="300px"
            />
            <AddUser />
          </Flex>
        </Flex>
      </Card.Header>
      <Card.Body>
        <Stack gap={4} align="stretch">
          {/* Using SimpleGrid instead of Table for better compatibility */}
          <Box overflowX="auto">
            <Box minW="800px">
              {/* Table Header */}
              <Flex
                bg="gray.50"
                p={3}
                borderBottom="2px"
                borderColor="gray.200"
                fontWeight="semibold"
              >
                <Box flex="1">Full Name</Box>
                <Box flex="1">Email</Box>
                <Box flex="1.5">Roles</Box>
                <Box w="100px">Permissions</Box>
                <Box w="100px">Status</Box>
                <Box w="150px">Actions</Box>
              </Flex>

              {/* Table Body */}
              <Stack gap={0} align="stretch">
                {filteredUsers?.map((user: UserWithRoles) => (
                  <Flex
                    key={user.id}
                    p={3}
                    borderBottom="1px"
                    borderColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                    align="center"
                  >
                    <Box flex="1">
                      <Text color={!user.full_name ? "gray.500" : "inherit"}>
                        {user.full_name || "N/A"}
                      </Text>
                    </Box>
                    <Box flex="1">
                      <Text truncate>{user.email}</Text>
                    </Box>
                    <Box flex="1.5">
                      <Flex wrap="wrap" gap={2}>
                        {Array.isArray(user.roles) && user.roles.length > 0 ? (
                          user.roles.map((userRole: UserRoleResponse) => (
                            <Badge
                              key={userRole.role.id}
                              colorScheme={
                                userRole.role.is_system_role ? "blue" : "purple"
                              }
                              size="sm"
                            >
                              {userRole.role.display_name || userRole.role.name}
                            </Badge>
                          ))
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            No roles
                          </Text>
                        )}
                      </Flex>
                    </Box>
                    <Box w="100px">
                      <Badge variant="outline" size="sm">
                        {user.effective_permissions?.length || 0}
                      </Badge>
                    </Box>
                    <Box w="100px">
                      <Badge
                        colorScheme={user.is_active ? "green" : "red"}
                        size="sm"
                      >
                        {user.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </Box>
                    <Box w="150px">
                      <Flex gap={2}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            console.log(
                              "Edit button clicked for user:",
                              user.email,
                            )
                            setEditingUser(user)
                          }}
                        >
                          <FiEdit />
                        </Button>
                        {user.is_active ? (
                          <Button
                            size="sm"
                            colorScheme="orange"
                            variant="outline"
                            onClick={() =>
                              deactivateUserMutation.mutate(user.id)
                            }
                            disabled={deactivateUserMutation.isPending}
                          >
                            <FiUserX />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            colorScheme="green"
                            variant="outline"
                            onClick={() => activateUserMutation.mutate(user.id)}
                            disabled={activateUserMutation.isPending}
                          >
                            <FiUserCheck />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDeleteUser(user)}
                          disabled={deleteUserMutation.isPending}
                        >
                          <FiTrash />
                        </Button>
                      </Flex>
                    </Box>
                  </Flex>
                ))}
              </Stack>
            </Box>
          </Box>

          {searchQuery && filteredUsers.length === 0 && (
            <Text textAlign="center" color="gray.500" mt={4}>
              No users found matching "{searchQuery}"
            </Text>
          )}
        </Stack>
      </Card.Body>

      {/* User Role Management Modal */}
      {editingUser && (
        <>
          {console.log(
            "Rendering modal for user:",
            editingUser.email,
            "Roles:",
            roles,
          )}
          <UserRoleManagementModal
            user={editingUser}
            availableRoles={roles}
            onRoleAssign={handleRoleAssign}
            onRoleRemove={handleRoleRemove}
            onClose={() => {
              console.log("Closing modal")
              setEditingUser(null)
            }}
          />
        </>
      )}
    </Card.Root>
  )
}
